import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff } from "lucide-react";
import { apiErr } from "@/lib/api";
import { BRANDING } from "@/config/branding";

const Brand = ({ dark, prominent = false }) => (
  <div className={`flex items-center gap-3 ${prominent ? "flex-col text-center" : ""}`}>
    <img src={BRANDING.logo} alt="K9 Manager" className={`${prominent ? "h-20 w-20 sm:h-24 sm:w-24" : "h-12 w-12"} rounded-2xl object-contain bg-white/90 p-1 shadow-sm`} />
    <div className="leading-tight">
      <div className={`font-head font-extrabold ${prominent ? "text-4xl sm:text-[2.5rem] tracking-tight" : "text-2xl"} ${dark ? "text-primary-foreground" : "text-foreground"}`}>{BRANDING.productName}</div>
      <div className={`text-xs font-semibold tracking-wide ${prominent ? "mt-2" : ""} ${dark ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{BRANDING.productSignature}</div>
    </div>
  </div>
);

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) nav("/", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password, remember);
      nav("/");
    } catch (e2) {
      setErr(apiErr(e2, "Accesso non riuscito."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={BRANDING.loginHero} alt="Educatore cinofilo con cane" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/40 to-primary/20" />
        <div className="absolute top-10 left-10"><Brand dark /></div>
        <div className="absolute bottom-12 left-10 right-10 text-primary-foreground">
          <h2 className="font-head text-4xl font-extrabold leading-tight">Meno lavoro amministrativo.<br />Più tempo con i cani.</h2>
          <p className="mt-3 text-primary-foreground/90 max-w-md">{BRANDING.subtitle}</p>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 py-6 lg:py-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4 lg:my-auto mx-auto">
          <div className="pb-4 lg:pb-6">
            <Brand prominent />
            <p className="mt-3 text-center text-sm leading-relaxed text-muted-foreground">{BRANDING.subtitle}</p>
          </div>
          <h1 className="font-head text-2xl font-bold">Accedi</h1>
          <div className="space-y-1">
            <Label>Email</Label>
            <Input data-testid="login-email" id="login-email" name="username" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="h-12" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          </div>
          <div className="space-y-1">
            <Label>Password</Label>
            <div className="relative">
              <Input data-testid="login-password" id="login-password" name="password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                className="h-12 pr-11" autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
              <button type="button" data-testid="login-password-toggle" onClick={() => setShowPw((v) => !v)}
                title={showPw ? "Nascondi password" : "Mostra password"} aria-label={showPw ? "Nascondi password" : "Mostra password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <Link to="/forgot-password" className="block text-sm underline">Password dimenticata?</Link>
          {err && <p data-testid="login-error" className="text-sm text-destructive font-medium">{err}</p>}
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="login-remember-label">
            <Checkbox data-testid="login-remember" checked={remember} onCheckedChange={(v) => setRemember(!!v)} />
            <span className="text-sm text-muted-foreground">Ricorda questo dispositivo</span>
          </label>
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-12 rounded-full text-base font-semibold">
            {loading ? "Accesso…" : "Entra"}
          </Button>
        </form>
      </div>
    </div>
  );
}
